import { useEffect, useState } from "react";
import { ExternalLink, Play, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { api } from "../../lib/api";

const TESLA_VIDEOS = [
  "https://customer-assets.emergentagent.com/job_lac-customs-booking/artifacts/citjybw1_IMG_9910.MOV",
  "https://customer-assets.emergentagent.com/job_lac-customs-booking/artifacts/eb0as2le_IMG_9909.MOV",
  "https://customer-assets.emergentagent.com/job_lac-customs-booking/artifacts/dhdw0m24_IMG_9906.MOV",
];
const RANGE_ROVER_VIDEOS = [
  "https://customer-assets.emergentagent.com/job_lac-customs-booking/artifacts/b9xziq2f_IMG_6218.MOV",
  "https://customer-assets.emergentagent.com/job_lac-customs-booking/artifacts/vfqvrh7i_IMG_6225.MOV",
];

const brands = [
  {
    name: "Tesla",
    direct: true, // direct video URLs (not TikTok)
    videos: TESLA_VIDEOS,
  },
  {
    name: "Range Rover",
    direct: true,
    videos: RANGE_ROVER_VIDEOS,
  },
  {
    name: "Mercedes",
    videos: [
      "https://vm.tiktok.com/ZNRpCdmsq/",
      "https://vm.tiktok.com/ZNRpQoEqd/",
      "https://vm.tiktok.com/ZNRpCfVBF/",
      "https://vm.tiktok.com/ZNRpCBonj/",
      "https://vm.tiktok.com/ZNRpCfD23/",
      "https://vm.tiktok.com/ZNRpCf4sY/",
      "https://vm.tiktok.com/ZNRpCNpqQ/",
    ],
  },
  {
    name: "BMW",
    videos: [
      "https://vm.tiktok.com/ZNRpCDpnV/",
      "https://vm.tiktok.com/ZNRpCf7Lw/",
      "https://vm.tiktok.com/ZNRpCkRTu/",
      "https://vm.tiktok.com/ZNRpCrbmN/",
      "https://vm.tiktok.com/ZNRpC2Q5f/",
      "https://vm.tiktok.com/ZNRpCJP9H/",
      "https://vm.tiktok.com/ZNRpC1s4G/",
    ],
  },
  {
    name: "Audi",
    videos: [
      "https://vm.tiktok.com/ZNRpC2uuX/",
      "https://vm.tiktok.com/ZNRpCR7GP/",
      "https://vm.tiktok.com/ZNRpC2E59/",
      "https://vm.tiktok.com/ZNRpCDYaw/",
      "https://vm.tiktok.com/ZNRpCDsSU/",
      "https://vm.tiktok.com/ZNRpC1ypQ/",
      "https://vm.tiktok.com/ZNRpChtn1/",
    ],
  },
  {
    name: "Volkswagen",
    videos: [
      "https://vm.tiktok.com/ZNRpCkBbH/",
      "https://vm.tiktok.com/ZNRpCD4pC/",
      "https://vm.tiktok.com/ZNRpC1wtk/",
      "https://vm.tiktok.com/ZNRpCJbU1/",
      "https://vm.tiktok.com/ZNRpCMGW1/",
      "https://vm.tiktok.com/ZNRpCRE8u/",
    ],
  },
  {
    name: "Porsche",
    videos: [
      "https://vm.tiktok.com/ZNRpCFxLv/",
      "https://vm.tiktok.com/ZNRpC1unc/",
    ],
  },
  {
    name: "Ford",
    videos: [
      "https://vm.tiktok.com/ZNRpC69XQ/",
      "https://vm.tiktok.com/ZNRpC8Swo/",
      "https://vm.tiktok.com/ZNRpCUgra/",
    ],
  },
  {
    name: "Vauxhall",
    videos: [
      "https://vm.tiktok.com/ZNRpC6go3/",
      "https://vm.tiktok.com/ZNRpCPdJR/",
    ],
  },
];

// Module-scoped cache so re-opening modals is instant
const oembedCache = new Map();

async function fetchOembed(url) {
  if (oembedCache.has(url)) return oembedCache.get(url);
  try {
    const res = await api.get(`/tiktok/oembed?url=${encodeURIComponent(url)}`);
    oembedCache.set(url, res.data);
    return res.data;
  } catch {
    oembedCache.set(url, null);
    return null;
  }
}

export default function BrandsStrip() {
  const [active, setActive] = useState(null);
  // brand-name → thumbnail_url for the first video of that brand
  const [brandThumbs, setBrandThumbs] = useState({});
  // url → oEmbed data, for the open modal
  const [videoMeta, setVideoMeta] = useState({});
  const [loadingMeta, setLoadingMeta] = useState(false);
  // Admin-uploaded clips merged into each brand
  const [adminClips, setAdminClips] = useState({}); // brand-name (lowercased) → [{media_url, ...}]

  // Pull admin-managed clips and merge them in
  useEffect(() => {
    api.get("/brand-clips")
      .then((r) => {
        const map = {};
        (r.data || []).forEach((c) => {
          const key = (c.brand || "").trim().toLowerCase();
          if (!key) return;
          (map[key] = map[key] || []).push(c);
        });
        setAdminClips(map);
      })
      .catch(() => {});
  }, []);

  // Build the brand list with admin clips merged in (admin clips become direct video brands too)
  const enrichedBrands = brands.map((b) => {
    const extras = adminClips[b.name.toLowerCase()] || [];
    if (!extras.length) return b;
    const extraUrls = extras.map((e) => e.media_url);
    return { ...b, direct: true, videos: [...extraUrls, ...b.videos] };
  });
  // Also expose any new brand keys admins added that aren't in the static list
  Object.entries(adminClips).forEach(([key, clips]) => {
    if (enrichedBrands.find((b) => b.name.toLowerCase() === key)) return;
    enrichedBrands.push({
      name: clips[0].brand,
      direct: true,
      videos: clips.map((c) => c.media_url),
    });
  });

  // Fetch the FIRST video's thumbnail for each brand on mount (one tile preview each).
  // Direct-video brands (Tesla / Range Rover) skip the oEmbed lookup — we use the file itself as the preview.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(
        enrichedBrands.map(async (b) => {
          if (b.direct) return;
          const data = await fetchOembed(b.videos[0]);
          if (data?.thumbnail_url) next[b.name] = data.thumbnail_url;
        })
      );
      if (!cancelled) setBrandThumbs((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminClips]);

  // When a brand modal opens, fetch all of its video oEmbeds in parallel
  useEffect(() => {
    if (!active) return;
    if (active.direct) {
      // Direct video brand — no oEmbed metadata needed
      setLoadingMeta(false);
      return;
    }
    let cancelled = false;
    setLoadingMeta(true);
    (async () => {
      const next = { ...videoMeta };
      await Promise.all(
        active.videos.map(async (url) => {
          const data = await fetchOembed(url);
          if (data) next[url] = data;
        })
      );
      if (!cancelled) {
        setVideoMeta(next);
        setLoadingMeta(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return (
    <section className="py-20 border-y border-white/5 bg-[#080808]" data-testid="brands-strip">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs tracking-[0.35em] text-[#D946EF] mb-3">/ BROWSE BY CAR</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tighter">
              We've worked on yours.
            </h2>
          </div>
          <p className="text-zinc-400 text-sm font-body max-w-xs">
            Tap a brand to watch real installs we've done — straight from TikTok.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {enrichedBrands.map((b) => {
            const thumb = brandThumbs[b.name];
            return (
              <button
                key={b.name}
                onClick={() => setActive(b)}
                data-testid={`brand-${b.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="group relative overflow-hidden rounded-sm border border-white/10 bg-[#0d0d0d] aspect-[5/3] hover:border-[#D946EF]/60 transition-all hover:-translate-y-0.5"
              >
                {b.direct ? (
                  <video
                    src={b.videos[0]}
                    muted
                    loop
                    autoPlay
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-80 transition-opacity"
                  />
                ) : thumb ? (
                  <img
                    src={thumb}
                    alt={`${b.name} install`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 transition-opacity"
                  />
                ) : null}
                <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-[#080808]/80 to-transparent" />
                <div className="relative h-full flex flex-col items-center justify-center p-5">
                  <span className="font-display font-bold text-lg sm:text-xl tracking-tight text-zinc-200 group-hover:text-white transition">
                    {b.name}
                  </span>
                  <span className="mt-1 text-[10px] tracking-[0.2em] text-zinc-500 group-hover:text-[#D946EF] transition">
                    {b.videos.length} VIDEOS →
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(open) => { if (!open) setActive(null); }}>
        <DialogContent
          data-testid="brand-modal"
          className="bg-[#0a0a0a] border-white/10 text-white max-w-4xl max-h-[88vh] overflow-y-auto"
        >
          {active && (
            <>
              <DialogHeader>
                <div className="text-xs tracking-[0.3em] text-[#D946EF]">
                  {active.direct ? "FROM THE LAC WORKSHOP" : "@LAC.LTD ON TIKTOK"}
                </div>
                <DialogTitle className="font-display font-bold text-3xl tracking-tighter mt-1">
                  {active.name} installs
                </DialogTitle>
                <div className="text-sm text-zinc-400 mt-1">
                  {active.videos.length} {active.direct ? "clips" : "reels"} — tap to {active.direct ? "play with sound" : "open it in TikTok"}.
                </div>
              </DialogHeader>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-2">
                {active.videos.map((url, i) => {
                  if (active.direct) {
                    return (
                      <video
                        key={url}
                        src={url}
                        controls
                        playsInline
                        preload="metadata"
                        data-testid={`brand-video-${i}`}
                        className="aspect-[9/16] w-full rounded-sm border border-white/10 bg-black object-cover"
                      />
                    );
                  }
                  const meta = videoMeta[url];
                  return (
                    <a
                      key={url}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`brand-video-${i}`}
                      className="group relative aspect-[9/16] rounded-sm overflow-hidden border border-white/10 bg-[#050505] hover:border-[#D946EF]/60 transition flex flex-col items-stretch"
                    >
                      {meta?.thumbnail_url ? (
                        <img
                          src={meta.thumbnail_url}
                          alt={meta?.title || `${active.name} install ${i + 1}`}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-zinc-600">
                          {loadingMeta ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

                      {/* play icon center */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#D946EF] group-hover:scale-110 transition">
                          <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" />
                        </div>
                      </div>

                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-sm bg-black/60 backdrop-blur-sm text-[10px] tracking-wide text-white font-semibold">
                        #{i + 1}
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-2.5">
                        <div className="font-body text-[11px] leading-snug text-zinc-200 line-clamp-2">
                          {meta?.title || `${active.name} install`}
                        </div>
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] tracking-[0.15em] text-[#D946EF]">
                          OPEN <ExternalLink className="w-2.5 h-2.5" />
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>

              <a
                href="https://www.tiktok.com/@lac.ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black font-semibold text-sm hover:bg-zinc-200 transition w-max"
              >
                See everything @lac.ltd <ExternalLink className="w-4 h-4" />
              </a>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
